import React, { Component } from 'react';
import GphApiClient from 'giphy-js-sdk-core';
import qp from 'query-parse';
import { withTranslation } from 'react-i18next';

import config from '../config';
import { debounce } from '../utils';

import Popout from './popout';
import Spinner from './spinner';
import SearchField from './search/search-field';
import GifThumb from './gif-thumb';

const giphyClient = GphApiClient(config.GIPHY_API_KEY);

const GIF_SIZE = {
    maxWidth: 325,
    maxHeight: 250,
    smallMaxWidth: 265
}

class GifPicker extends Component { 
    state = {
        visible: false,
        loading: true,
        fetched: false,
        searchTerm: '',
        suggestions: []
    }

    constructor(props) {
        super(props);

        this.debouncedFetch = debounce(this.fetch, 300, this);
    }

    handleToggleClick = () => {
        this.popoutCtrl && this.popoutCtrl.show();
    }

    handleSearchChange = searchTerm => {
        this.setState({ searchTerm });

        if (searchTerm !== this.state.searchTerm) {
            this.debouncedFetch(searchTerm);
        }
    }

    handleThumbClick = gifProps => {
        this.popoutCtrl && this.popoutCtrl.hide();
        const { url, ...params } = gifProps;

        this.props.onSelect && this.props.onSelect(`${gifProps.url}?${qp.toString(params)}`);
    }

    handlePopouShow = () => {
        if (!this.state.fetched) {
            this.fetch('');
        }

        this.setState({
            visible: true,
            fetched: true
        });

        setTimeout(() => {
            this.searchFieldCtrl && this.searchFieldCtrl.focus()
        }, 100);
    }

    handlePopoutHide = () => {
        this.setState({
            visible: false,
            loading: false,
            searchTerm: '',
            suggestions: this.trendingCache || []
        });
    }

    fetch(term) {
        let request;
        let trending = false;

        if (term === '') {
            trending = true;
            request = giphyClient.trending('gifs');
        } else {
            request = giphyClient.search('gifs', { q: term });
        }

        this.setState({
            loading: true
        });

        request.then(response => {
            if (this.unmounted) {
                return;
            }

            const suggestions = response.data.map(gif => {
                const ratio = gif.images.downsized.width / gif.images.downsized.height;
                let width = Math.min(gif.images.downsized.width, GIF_SIZE.maxWidth);
                let height = width / ratio;

                if (height > GIF_SIZE.maxHeight) {
                    height = GIF_SIZE.maxHeight;
                    width = height * ratio;
                }

                return {
                    width,
                    height,
                    smallwidth: Math.min(width, GIF_SIZE.smallMaxWidth),
                    smallheight: Math.min(width, GIF_SIZE.smallMaxWidth) / ratio,
                    url: gif.images.downsized.url
                }
            });

            if (trending) {
                this.trendingCache = suggestions;
            }

            this.setState({
                loading: false,
                suggestions
            });
        });
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    render() {
        const childrenWithProps = React.Children.map(this.props.children, child => {
            return React.cloneElement(child, {
                onClick: this.handleToggleClick
            });
        });

        const { t } = this.props;

        return (
            <React.Fragment>
                { childrenWithProps }

                <Popout className="large gif-picker-popout" defaultYPosition="top" provideController={ctrl => this.popoutCtrl = ctrl} onShow={this.handlePopouShow} onHide={this.handlePopoutHide}>
                    <div className="gif-picker">
                        <div className="popout-body">
                            <SearchField
                                expanded
                                placeholder={ t('gif.Search GIFs') }
                                value={this.state.searchTerm}
                                className="search-field-alt"
                                provideController={ctrl => this.searchFieldCtrl = ctrl}
                                onChange={this.handleSearchChange}
                            />

                            <div className={'gif-picker-suggestions' + (this.state.loading ? ' loading' : '')}>
                                {
                                    this.state.loading ? <Spinner /> : (
                                        <div className="suggestions-inner">
                                            {
                                                this.state.suggestions.map(gif => <GifThumb
                                                    key={gif.url}
                                                    url={gif.url}
                                                    width={gif.width}
                                                    height={gif.height}
                                                    gifProps={gif}
                                                    selectable={true}
                                                    showAttribution={false}
                                                    onClick={this.handleThumbClick}
                                                />)
                                            }
                                        </div>
                                    )
                                }
                            </div>

                            <div className="gif-attribution">
                                { t('Powered by', { name: 'GIPHY' }) }
                            </div>
                        </div>
                    </div>
                </Popout>
            </React.Fragment>
        )
    }
}

export default withTranslation('common')(GifPicker);
